import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import Button from "./Button";

function getHeaders() {
	const token = localStorage.getItem("token");
	return token ? { Authorization: `Bearer ${token}` } : {};
}

function ClientUniverseAccessDialog({ open, onClose, clientId }) {
	const [universes, setUniverses] = useState([]);
	const [accessibleIds, setAccessibleIds] = useState(new Set());
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	const fetchData = useCallback(async () => {
		if (!clientId) return;
		setIsLoading(true);
		setError("");
		try {
			const [uniRes, accRes] = await Promise.all([
				fetch("/v1/admin/universes", { headers: getHeaders() }),
				fetch(`/v1/admin/universes/client/${clientId}/accessible`, { headers: getHeaders() }),
			]);
			if (uniRes.ok && accRes.ok) {
				setUniverses(await uniRes.json());
				setAccessibleIds(new Set(await accRes.json()));
			} else {
				setError("Errore nel recupero dati");
			}
		} catch { setError("Errore di connessione"); }
		finally { setIsLoading(false); }
	}, [clientId]);

	useEffect(() => {
		if (open && clientId) fetchData();
	}, [open, clientId, fetchData]);

	useEffect(() => {
		if (!open) { setError(""); }
	}, [open]);

	const handleTransfer = useCallback(async (universeId) => {
		if (!window.confirm(`Trasferire ownership di "${universeId}" a "${clientId}"?`)) return;
		setError("");
		try {
			const res = await fetch(`/v1/admin/universes/${universeId}/transfer`, {
				method: "PUT",
				headers: { "Content-Type": "application/json", ...getHeaders() },
				body: JSON.stringify({ clientId }),
			});
			if (res.ok) {
				fetchData();
			} else {
				setError(await res.text());
			}
		} catch { setError("Errore di connessione"); }
	}, [clientId, fetchData]);

	const handleToggle = useCallback(async (universeId, isOwner) => {
		if (isOwner) return;
		const currentlyHasAccess = accessibleIds.has(universeId);
		setError("");

		try {
			let res;
			if (currentlyHasAccess) {
				res = await fetch(`/v1/admin/universes/${universeId}/access/${clientId}`, {
					method: "DELETE",
					headers: { ...getHeaders() },
				});
			} else {
				res = await fetch(`/v1/admin/universes/${universeId}/access`, {
					method: "POST",
					headers: { "Content-Type": "application/json", ...getHeaders() },
					body: JSON.stringify({ clientId }),
				});
			}

			if (res.ok) {
				setAccessibleIds((prev) => {
					const next = new Set(prev);
					if (currentlyHasAccess) next.delete(universeId);
					else next.add(universeId);
					return next;
				});
			} else {
				setError(await res.text());
			}
		} catch { setError("Errore di connessione"); }
	}, [accessibleIds, clientId]);

	if (!open) return null;

	return (
		<dialog open className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4" aria-modal="true" onCancel={onClose}>
			<div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
				<div className="border-b border-slate-200 px-5 py-4">
					<h2 className="text-lg font-semibold text-slate-900">Universe Access</h2>
					<p className="mt-1 text-sm text-slate-600">
						Gestisci universi accessibili da <strong>{clientId}</strong>
					</p>
				</div>

				<div className="px-5 py-4">
					{error && <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}

					{isLoading ? (
						<div className="flex items-center justify-center py-10">
							<Loader2 className="h-5 w-5 animate-spin text-slate-400" />
						</div>
					) : universes.length > 0 ? (
						<div className="max-h-[400px] overflow-y-auto border border-slate-100 rounded-md">
							<table className="w-full text-sm">
								<thead className="bg-slate-50 sticky top-0">
									<tr className="border-b border-slate-200">
										<th className="px-3 py-2 w-10"></th>
										<th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Universe</th>
										<th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-600">Owner</th>
										<th className="px-3 py-2 w-20"></th>
									</tr>
								</thead>
								<tbody>
									{universes.map((u) => {
										const isOwner = u.clientId === clientId;
										const hasAccess = accessibleIds.has(u.universeId);
										return (
											<tr key={u.universeId} className="border-b border-slate-50 hover:bg-slate-50">
												<td className="px-3 py-2 text-center">
													<input
														type="checkbox"
														checked={hasAccess}
														disabled={isOwner}
														onChange={() => handleToggle(u.universeId, isOwner)}
														className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
													/>
												</td>
												<td className="px-3 py-2">
													<span className="font-mono text-xs text-slate-800">{u.universeId}</span>
													{u.name && <span className="ml-2 text-xs text-slate-500">({u.name})</span>}
												</td>
												<td className="px-3 py-2">
													<span className={`text-xs ${isOwner ? "font-semibold text-slate-800" : "text-slate-500"}`}>
														{u.clientId}
													</span>
												</td>
												<td className="px-3 py-2 text-right">
													{isOwner ? (
														<span className="text-xs font-medium text-blue-600">owner</span>
													) : (
														<button
															type="button"
															onClick={() => handleTransfer(u.universeId)}
															className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
															title="Trasferisci ownership a questo client"
														>
															Transfer
														</button>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					) : (
						<div className="py-10 text-center text-sm text-slate-500">
							Nessun universo presente nel sistema.
						</div>
					)}
				</div>

				<div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
					<Button variant="outline" onClick={onClose}>Close</Button>
				</div>
			</div>
		</dialog>
	);
}

ClientUniverseAccessDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	clientId: PropTypes.string,
};

export default ClientUniverseAccessDialog;
